// Radio station source (contract D.4.2, C.9). WEB-CORE owns each instance and
// keeps it in queue.source.radio; fetch-more for a radio queue calls more().
//
// createRadioSource(band, params, { minutes }) -> {
//   start(): Promise<Segment[]>    first page (drops any cursor), resets exhausted
//   more(): Promise<Segment[]>     next page from the kept cursor; [] once exhausted
//   vote(item, +1|-1) -> { skipChainId: string|null }
//   params(): object               station params incl. seed, more, less (for buildRadioLink)
//   label(): string                station label from the server, or 'Radio'
//   exhausted: bool                (live getter)
// }
// params = { mode, vibe, era, from, to, sbd, song, order, path, land, more, less, seed }
// Every page asks for `minutes` of music (default api.RADIO_PAGE_MINUTES, about
// 2.5 hours; the server caps a page at 40 segments). The page length is not a
// station param, so params() and radio links never carry it.
// Errors propagate from api.radio (Error with .userMessage).

import * as api from '../api';

const FEEDBACK_MAX = 20;

// Also used by SongPage for Song Radio seeds (the server needs one on the first call).
export const newSeed = () => {
  try {
    const c = typeof window !== 'undefined' ? window.crypto || window.msCrypto : null;
    if (c && c.getRandomValues) {
      const a = new Uint32Array(1);
      c.getRandomValues(a);
      return a[0];
    }
  } catch (e) {
    /* fall through */
  }
  return Math.floor(Math.random() * 4294967296) >>> 0;
};

const keyList = (v) => (Array.isArray(v) ? v.filter((k) => typeof k === 'string' && k) : []);

// Push keys to the end of `list` (FIFO, newest last), capped at 20.
const pushKeys = (list, keys) => list.filter((k) => keys.indexOf(k) < 0).concat(keys).slice(-FEEDBACK_MAX);

export const createRadioSource = (band, params, opts) => {
  const pageMinutes = (opts && opts.minutes) || api.RADIO_PAGE_MINUTES;
  const base = { ...(params || {}) };
  delete base.minutes;
  delete base.n;
  const seedNum = Number(base.seed);
  base.seed = base.seed != null && base.seed !== '' && Number.isFinite(seedNum) ? seedNum >>> 0 : newSeed();
  let moreKeys = keyList(base.more).slice(-FEEDBACK_MAX); // thumbs up, newest last
  let lessKeys = keyList(base.less).slice(-FEEDBACK_MAX); // thumbs down, newest last
  delete base.more;
  delete base.less;

  let cursor = null;
  let started = false;
  let exhausted = false;
  let station = null;
  let gen = 0; // bumped by start(); results of an older generation are dropped
  let inflight = null; // { kind: 'start'|'more', promise }

  // Song keys per chain, and per track, for vote(): QueueItems carry chainId,
  // sid, tapeId and trackIdx but not songKeys.
  const byChain = new Map();
  const byTrack = new Map();
  const trackKey = (sid, tapeId, trackIdx) => sid + '|' + tapeId + '|' + trackIdx;

  const remember = (segments) => {
    segments.forEach((seg) => {
      const keys = keyList(seg && seg.songKeys);
      if (seg.chainId) byChain.set(seg.chainId, keys);
      (seg.tracks || []).forEach((t) => byTrack.set(trackKey(seg.sid, seg.tapeId, t.trackIdx), keys));
    });
  };

  const request = (kind, withCursor) => {
    const g = gen;
    const promise = api
      .radio(band, { ...base, more: moreKeys.slice(), less: lessKeys.slice(), minutes: pageMinutes }, withCursor ? cursor : null)
      .then((res) => {
        if (g !== gen) return [];
        const segments = Array.isArray(res && res.segments) ? res.segments : [];
        cursor = res && res.cursor ? res.cursor : null;
        exhausted = !!(res && res.exhausted) || !cursor;
        if (res && res.station) station = res.station;
        started = true;
        remember(segments);
        return segments;
      })
      .finally(() => {
        if (inflight && inflight.promise === promise) inflight = null;
      });
    inflight = { kind, promise };
    return promise;
  };

  const start = () => {
    gen++;
    cursor = null;
    exhausted = false;
    return request('start', false);
  };

  const more = () => {
    if (inflight) {
      // a concurrent caller gets nothing rather than the same segments twice;
      // more() during start() fetches the page after start's
      return inflight.kind === 'start' ? inflight.promise.then(() => (exhausted ? [] : request('more', true))) : inflight.promise.then(() => []);
    }
    if (!started) return start();
    if (exhausted) return Promise.resolve([]);
    return request('more', true);
  };

  const vote = (item, dir) => {
    if (!item || !dir) return { skipChainId: null };
    const keys = (item.chainId && byChain.get(item.chainId)) || byTrack.get(trackKey(item.sid, item.tapeId, item.trackIdx)) || [];
    if (keys.length) {
      if (dir > 0) {
        moreKeys = pushKeys(moreKeys, keys);
        lessKeys = lessKeys.filter((k) => keys.indexOf(k) < 0);
      } else {
        lessKeys = pushKeys(lessKeys, keys);
        moreKeys = moreKeys.filter((k) => keys.indexOf(k) < 0);
      }
    }
    return { skipChainId: dir < 0 && item.chainId ? item.chainId : null };
  };

  return {
    start,
    more,
    vote,
    params: () => ({ ...base, more: moreKeys.slice(), less: lessKeys.slice() }),
    label: () => (station && station.label ? station.label : 'Radio'),
    get exhausted() {
      return exhausted;
    },
  };
};

export default createRadioSource;
